import {  mcStatus } from '@/utils';

/**
 * A fancy console logger to make it easier to see custom messages
 */
export const mcLogger = {
  /**
   * Known colors for files.
   * Update and use this to have an easier time keeping track of which colors are already in use to avoid duplicate color usage.
   */
  colors: {
    "AuthService": "slateblue",
    "CounterApi": "cadetblue",
    "CountHub": "blueviolet",
    "CountMeals": "darkcyan",
    "Dashboard": "springgreen",
    "McMealWizard": "yellow",
    "McMslPrompt": "orange",
    "McSync": "lightgreen",
    "MealCountCheck": "lavender",
    "MealCountDtoViewModel": "lightslategray",
    "MealLoader": "darkmagenta",
    "SiteService": "aqua",
    "SyncMealsDownCommand": "darkgoldenrod",
    "SyncService": "green",
  },

  /**
   * Creates a fancy colored logger to route through
   * @param name The name of the logger, usually the file or component name
   * @param color The color your want it to appear as in the console output
   * @example
   *  private console: Console = mcLogger.getConsole('count-hub.ts', 'cornflowerblue');
   *  this.console.debug('Your message');
   */
  getConsole(name: string, color: string): Console {
    // If we are in DevMode, then it's okay to output the debug and info statement, but otherwise I want to hide them
    const isInDevMode:boolean = mcStatus.isInDevMode();

    const newConsole : Console = Object.create(console);
    newConsole.debug = isInDevMode ? console.debug.bind(console, `%c[${name}]`, `color: ${color}`) : function(){};
    newConsole.info = isInDevMode ? console.info.bind(console, `%c[${name}]`, `color: ${color}`) : function(){};
    newConsole.log = console.log.bind(console, `%c[${name}]`, `color: ${color}`);
    newConsole.warn = console.warn.bind(console, `[${name}]`);
    newConsole.error = console.error.bind(console, `[${name}]`);
    newConsole.time = (label?: string) => console.time(`[${name}] ${label}`);
    newConsole.timeEnd = (label?: string) => console.timeEnd(`[${name}] ${label}`);

    return newConsole;
  },

  /**
   * Creates a fancy colored logger to route through. Uses the the name to determine the color.
   * NOTE: This really just returns a color that is too dark, consider using getConsole instead
   * @param name The name of the logger, usually the file or component name
   */
  getConsoleWithAutoColor(name: string): Console {
    /* TODO: This really just returns a color that is too dark.
     *  I could change it so the first two characters of the HEX code are something like 'ff' which would make it brighter, but I would still have the problem of everything looking the same.
     */
    // Convert the name into a hex string and then send the first 6 characters after '0x' to the getConsole function
    let textEncoder = new TextEncoder();
    let utf8ByteArray = textEncoder.encode(name);
    let hexString = this.toHexString(utf8ByteArray);
    let trimmedHexString = `#${hexString.substring(2, 8)}`;

    return this.getConsole(name, trimmedHexString);
  },

  toHexString(byteArray: Uint8Array) {
    var s = '0x';
    byteArray.forEach(function(byte) {
      s += ('0' + (byte & 0xFF).toString(16)).slice(-2);
    });
    return s;
  }
}
